import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment'; // Already present
import { Button, Modal, Spin, Alert, Typography, Card, Descriptions, Empty, Tag, Space, message, Tooltip } from 'antd'; // Added message, Tooltip
import { FormOutlined, HistoryOutlined, CheckCircleOutlined } from '@ant-design/icons'; // Added CheckCircleOutlined
import { useAuth } from '../context/AuthContext'; // Already present
import StructuredLifestyleHistoryForm from './StructuredLifestyleHistoryForm'; // Import the form

const { Text, Title } = Typography;

// Helper function to render array data (like work environment)
const renderArrayData = (dataArray) => {
  if (!dataArray || dataArray.length === 0) {
    return <Text type="secondary">N/A</Text>;
  }
  return (
    <Space direction="vertical" size="small">
      {dataArray.map((item, index) => <Tag key={index} color="red">{item}</Tag>)}
    </Space>
  );
};

// Helper to render substance use
const renderSubstanceUse = (useData) => {
    if (!useData || !useData.status || useData.status === 'Never') {
        return <Text type="secondary">Never</Text>;
    }
    return (
        <Space direction="vertical" size="small">
            <Text><Tag color={useData.status === 'Current' ? 'red' : 'orange'}>{useData.status}</Tag></Text>
            {useData.details && <Text type="secondary">Details: {useData.details}</Text>}
        </Space>
    );
};


const LifestyleHistorySection = ({ patientId }) => {
  const [lifestyleData, setLifestyleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [lastReviewed, setLastReviewed] = useState(null); // Added state
  const { user } = useAuth(); // Already present

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch patient data, populating review user
      const response = await axios.get(`/patients/${patientId}?populate=lifestyleHistoryLastReviewed.user`);
      const patientData = response.data.data.patient;
      setLifestyleData(patientData?.structuredLifestyleHistory || null);
      setLastReviewed(patientData?.lifestyleHistoryLastReviewed || null); // Set review status
    } catch (err) {
      console.error("Error fetching lifestyle history data:", err);
      setError(err.response?.data?.message || 'Failed to fetch lifestyle history data.');
      setLifestyleData(null);
      setLastReviewed(null); // Clear on error
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Modal Handlers
  const showModal = () => { setIsModalVisible(true); };
  const handleModalCancel = () => { setIsModalVisible(false); };

  const handleSaveSuccess = () => {
    setIsModalVisible(false);
    message.success("Checklist saved successfully!"); // Already shown in form, but good for consistency
    fetchData(); // Re-fetch data to update the display
  };

  // --- Mark as Reviewed Logic ---
  const handleMarkReviewed = async () => {
    if (!user || !user._id) {
      message.error("Cannot mark as reviewed: User not logged in.");
      return;
    }
    const reviewData = { user: user._id, date: new Date().toISOString() };
    setLoading(true);
    try {
      await axios.patch(`/patients/${patientId}`, { lifestyleHistoryLastReviewed: reviewData });
      message.success('Lifestyle History marked as reviewed.');
      setLastReviewed({ user: { _id: user._id, name: user.name }, date: reviewData.date });
    } catch (err) {
      console.error("Error marking lifestyle history as reviewed:", err);
      message.error(err.response?.data?.message || 'Failed to mark as reviewed.');
    } finally {
      setLoading(false);
    }
  };

  const canModify = user && ['Admin', 'MD', 'Nursing'].includes(user.role);

  // Check if any meaningful data exists to display
  const hasData = lifestyleData && Object.values(lifestyleData).some(value => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object' && value !== null) { // Check substance use objects
          return value.status && value.status !== 'Never';
      }
      return value !== null && value !== undefined && value !== '';
  });


  return (
    <Spin spinning={loading}>
      {/* Removed outer Card */}
      <>
        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

        {/* Standard Action Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: '10px' }}>
          <Space>
            {canModify && (
              <Button type="primary" icon={<HistoryOutlined />} onClick={showModal}>
                Update Lifestyle History Checklist
              </Button>
            )}
            {/* Added Mark as Reviewed Button */}
            {canModify && (
              <Tooltip title="Mark this section as reviewed by you right now">
                <Button icon={<CheckCircleOutlined />} onClick={handleMarkReviewed}>
                  Mark as Reviewed
                </Button>
              </Tooltip>
            )}
          </Space>
          {/* Display Last Reviewed Status */}
          <div style={{ textAlign: 'right' }}>
            {lastReviewed?.date ? (
              <Text type="secondary" style={{ fontSize: '0.85em' }}>
                Last Reviewed by {lastReviewed.user?.name || 'Unknown User'} on {moment(lastReviewed.date).format('L LT')}
                {/* Add user name if available and populated */}
                {/* {lifestyleData.lastUpdatedBy ? ` by ${lifestyleData.lastUpdatedBy.name}` : ''} */}
              </Text>
            ) : (
               !loading && !error && <Text type="secondary" style={{ fontSize: '0.85em' }}>Not yet reviewed</Text> // Changed text
            )}
          </div>
        </div>

        {/* Display Data */}
        {!loading && !error && !hasData && (
             <Empty description="No lifestyle history data recorded." image={Empty.PRESENTED_IMAGE_SIMPLE} />
         )}

        {/* Standard Title */}
        <Title level={5} style={{ marginTop: '10px', marginBottom: '10px' }}>Lifestyle Details</Title>

        {lifestyleData && hasData && (
          /* Changed column prop to force single column layout */
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Tình trạng quan hệ">{lifestyleData.relationshipStatus || <Text type="secondary">N/A</Text>}</Descriptions.Item>
            <Descriptions.Item label="Tình trạng cư trú">{lifestyleData.livingSituation || <Text type="secondary">N/A</Text>}</Descriptions.Item>
            <Descriptions.Item label="Trình độ học vấn">{lifestyleData.educationLevel || <Text type="secondary">N/A</Text>}</Descriptions.Item>
            <Descriptions.Item label="Tình trạng nghề nghiệp">{lifestyleData.employmentStatus || <Text type="secondary">N/A</Text>}</Descriptions.Item>
            <Descriptions.Item label="Nghề nghiệp">{lifestyleData.occupation || <Text type="secondary">N/A</Text>}</Descriptions.Item>
            <Descriptions.Item label="Thuốc lá">{renderSubstanceUse(lifestyleData.tobaccoUse)}</Descriptions.Item>
            <Descriptions.Item label="Rượu bia">{renderSubstanceUse(lifestyleData.alcoholUse)}</Descriptions.Item>
            <Descriptions.Item label="Chất kích thích khác">{renderSubstanceUse(lifestyleData.drugUse)}</Descriptions.Item>
            <Descriptions.Item label="Môi trường làm việc" span={2}>{renderArrayData(lifestyleData.workEnvironment)}</Descriptions.Item>
            <Descriptions.Item label="Ghi chú chung" span={2}>{lifestyleData.notes || <Text type="secondary">N/A</Text>}</Descriptions.Item>
          </Descriptions>
        )}

        {/* Modal for Editing */}
        <Modal
          title="Update Lifestyle History Checklist"
          visible={isModalVisible}
          onCancel={handleModalCancel}
          footer={null} // Form has its own buttons
          width={800}
          destroyOnClose
        >
          {isModalVisible && patientId && (
            <StructuredLifestyleHistoryForm
              patientId={patientId}
              onSaveSuccess={handleSaveSuccess}
              onCancel={handleModalCancel}
            />
          )}
        </Modal>
      </>
    </Spin>
  );
};

export default LifestyleHistorySection;
